import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import Footer from "./Footer";
import Navbar from "./Navbar";
import WhyChooseUs from "./WhyChooseUs";
import LoanCalculator from "./LoanCalculator";
import LoanSteps from "./LoanSteps";
// import CheckEligibility from "./CheckEligibility";
import Testimonials from "./Testimonials";
// import HomeFAQ from "./HomeFAQ";
import FinancialInsights from "./FinancialInsights";
import SecondFinancialInsights from "./SecondFinancialInsights";

import herosectionbg from "../assets/herosectionbg.png";

function CompanyLanding() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.replace("#", "");
    if (!id) return;
    const t = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 100);

    return () => window.clearTimeout(t);
  }, [location.hash]);
  return (
    <div className="min-h-screen w-full bg-[#D6EDFE] text-[var(--text-primary)] relative overflow-x-hidden font-sans selection:bg-[var(--primary)] selection:text-white">
      <div className="relative z-10 flex w-full flex-col items-center">
        <Navbar />

        {/* Hero Section Card Wrapper */}
        <div className="w-full bg-white px-0 md:px-8 lg:pt-18 md:pb-6 flex justify-center items-center">
          <section className="w-full max-w-[1400px]">
            {/* Desktop Hero Section: Hidden on mobile, visible on md and up */}
            <div
              className="hidden md:flex w-full rounded-b-[32px] md:rounded-b-[40px] min-h-[580px] md:min-h-[680px] lg:min-h-[720px] px-4 flex-col justify-end items-center overflow-hidden relative shadow-sm"
              style={{
                backgroundImage: `url(${herosectionbg})`,
                backgroundSize: "cover",
                backgroundPosition: "center 65%",
                backgroundRepeat: "no-repeat",
              }}
            >
              {/* Reddish/Peach overlay tint & bottom fade */}
              <div
                className="absolute inset-0 z-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(to bottom, rgba(222, 50, 38, 0.22) 0%, rgba(222, 50, 38, 0.14) 50%, rgba(255, 255, 255, 0.9) 85%, #FFFFFF 100%)",
                }}
              />

              <div className="max-w-[1200px] mx-auto w-full relative z-10 flex flex-col items-center text-center gap-5 px-4 md:px-8 pt-32 md:pt-52 pb-12 md:pb-16">
                {/* Badge */}
                <div className="inline-flex items-center justify-center bg-white border border-[#FBE3DF] px-4 py-1.5 rounded-full shadow-[0_2px_8px_rgba(222,50,38,0.04)]">
                  <span className="text-[#DE3226] text-[11px] md:text-xs font-black uppercase italic tracking-widest">
                    FAST. SECURE. PAPERLESS.
                  </span>
                </div>

                {/* Heading */}
                <h1 className="text-2xl sm:text-2xl md:text-6xl lg:text-[60px] font-black text-white leading-[1.05] tracking-tight max-w-4xl flex flex-col items-center italic">
                  <span className="drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)] text-center">
                    Get the cash you need,
                  </span>
                  <span className="inline-block bg-[#DE3226] text-white px-6 py-1.5 md:py-2 rounded-[8px] mt-1.5 shadow-[0_4px_12px_rgba(222,50,38,0.3)] text-center not-italic">
                    <span className="italic inline-block">When you need it.</span>
                  </span>
                </h1>

                {/* Subheading */}
                <p className="text-neutral-800 text-sm sm:text-base md:text-[17px] font-semibold leading-relaxed max-w-[720px] mt-4 px-2 text-center">
                  Apply online in just 2 minutes with minimal documentation. Get
                  quick approval and funds transferred directly to your bank
                  account.
                </p>

                {/* CTA Button */}
                <div className="mt-4">
                  <a
                    href={import.meta.env.VITE_WEBSITE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#111111] hover:bg-black text-white px-8 py-4 rounded-[8px] font-bold text-base md:text-[17px] inline-flex items-center gap-2.5 transition-all shadow-md active:scale-95 hover:shadow-lg group"
                  >
                    <span>Check Eligibility</span>
                    <svg
                      className="w-5 h-5 transition-transform group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Mobile Hero Section: Visible on mobile, hidden on md and up */}
            <div className="flex md:hidden w-full flex-col items-center pt-24 pb-8 bg-white">
              {/* Heading */}
              <h1 className="text-3xl sm:text-3xl font-black text-[#111111] leading-[1.15] tracking-tight text-center italic flex flex-col items-center px-4 pb-5">
                <span>Get the cash you need,</span>
                <span className="text-[#DE3226]">When you need it.</span>
              </h1>

              {/* Subheading */}
              <p className="text-neutral-800 text-sm font-medium leading-relaxed text-center max-w-[400px] px-4 pb-5">
                Apply online in just 2 minutes with minimal documentation.
                Get quick approval and funds transferred directly to your
                bank account.
              </p>

              {/* Image Container with overlay & CTA */}
              <div className="w-full relative flex flex-col items-center mt-3 px-4">
                <div
                  className="w-full aspect-[4/5] rounded-[24px] overflow-hidden relative shadow-sm"
                  style={{
                    backgroundImage: `url(${herosectionbg})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center 45%",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  {/* Reddish/Peach overlay tint & bottom fade */}
                  <div
                    className="absolute inset-0 z-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(to bottom, rgba(222, 50, 38, 0.25) 0%, rgba(222, 50, 38, 0.12) 50%, rgba(255, 255, 255, 0.8) 85%, #FFFFFF 100%)",
                    }}
                  />
                </div>

                {/* CTA Button overlapping the bottom edge */}
                <div className="absolute bottom-[10vh] z-10 w-full flex justify-center px-4">
                  <a
                    href={import.meta.env.VITE_WEBSITE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#111111] hover:bg-black text-white px-8 py-4 rounded-[16px] font-bold text-base inline-flex items-center justify-center gap-2.5 transition-all shadow-lg active:scale-95 w-[90%] max-w-[320px]"
                  >
                    <span>Check Eligibility</span>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Stats Bar (Mobile Only) */}
              <div className="w-full bg-[#DE3226] mt-0 py-6 px-1 font-['Golos_Text',_sans-serif]">
                <div className="grid grid-cols-4 text-white">
                  {/* Stat 1 */}
                  <div className="flex flex-col items-center text-center px-1 border-r border-white/20">
                    <span className="text-lg sm:text-xl  leading-none">₹150 Cr</span>
                    <span className="text-white/90 text-[8px] xs:text-[9px]  mt-2.5 tracking-tight text-center whitespace-nowrap">Total Loans Disbursed</span>
                  </div>
                  {/* Stat 2 */}
                  <div className="flex flex-col items-center text-center px-1 border-r border-white/20">
                    <span className="text-lg sm:text-xl  leading-none">4 mins</span>
                    <span className="text-white/90 text-[8px] xs:text-[9px]  mt-2.5 tracking-tight text-center whitespace-nowrap">Avg. Approval Time</span>
                  </div>
                  {/* Stat 3 */}
                  <div className="flex flex-col items-center text-center px-1 border-r border-white/20">
                    <span className="text-lg sm:text-xl  leading-none">18L+</span>
                    <span className="text-white/90 text-[8px] xs:text-[9px]  mt-2.5 tracking-tight text-center whitespace-nowrap">Happy Borrowers</span>
                  </div>
                  {/* Stat 4 */}
                  <div className="flex flex-col items-center text-center px-1">
                    <span className="text-lg sm:text-xl  leading-none">98%</span>
                    <span className="text-white/90 text-[8px] xs:text-[9px]  mt-2.5 tracking-tight text-center whitespace-nowrap">Approval Rate</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
          
        </div>
        

        {/* Home Sections Container */}
        <div className="w-full bg-white relative z-10">
          <FinancialInsights />
          <WhyChooseUs />
          <LoanCalculator />
          <LoanSteps />
          <Testimonials />
          <SecondFinancialInsights />

          {/* <CheckEligibility />
          <HomeFAQ /> */}
        </div>

        <Footer />
      </div>
    </div>
  );
}

export default CompanyLanding;
